/**
 * Snell's law and its consequences: the refraction angle, total internal reflection,
 * the critical and Brewster angles, and Fresnel reflectance.
 *
 * Angles are in degrees, measured from the normal to the surface. n₁ is the index of the
 * medium the light is in, n₂ the index of the medium it enters.
 *
 * Refractive indices are for the sodium D line (589 nm) from Hecht, *Optics*, except the
 * two optical glasses, which are Schott's catalogue nd (587.6 nm). Gases are at 0 °C and
 * 1 atm, liquids at 20 °C.
 */

export type MaterialId =
  | "vacuum"
  | "air"
  | "ice"
  | "water"
  | "ethanol"
  | "vegetableOil"
  | "glycerol"
  | "fusedSilica"
  | "acrylic"
  | "benzene"
  | "crownGlass"
  | "salt"
  | "carbonDisulfide"
  | "sapphire"
  | "flintGlass"
  | "diamond";

/** Refractive index of each listed material, from least to greatest. */
export const MATERIAL_INDEX: Record<MaterialId, number> = {
  vacuum: 1,
  air: 1.000_293,
  ice: 1.31,
  water: 1.333,
  ethanol: 1.361,
  vegetableOil: 1.47,
  glycerol: 1.4729,
  fusedSilica: 1.458,
  acrylic: 1.49,
  benzene: 1.501,
  crownGlass: 1.5168,
  salt: 1.544,
  carbonDisulfide: 1.628,
  sapphire: 1.77,
  flintGlass: 1.7283,
  diamond: 2.417,
};

/** The materials sorted by index, for the reference table. */
export const MATERIAL_ORDER: readonly MaterialId[] = (
  Object.keys(MATERIAL_INDEX) as MaterialId[]
).sort((a, b) => MATERIAL_INDEX[a] - MATERIAL_INDEX[b]);

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

export type SnellError = "invalid-index" | "invalid-angle" | "no-solution";

export type SnellTarget = "theta2" | "n2" | "theta1" | "n1";

export interface SnellKnown {
  n1?: number;
  n2?: number;
  theta1?: number;
  theta2?: number;
}

export type SnellResult =
  | { ok: true; value: number }
  | { ok: false; error: SnellError };

function validIndex(n: number | undefined): n is number {
  return n !== undefined && Number.isFinite(n) && n >= 1;
}

function validAngle(a: number | undefined): a is number {
  return a !== undefined && Number.isFinite(a) && a >= 0 && a < 90;
}

/**
 * Solve n₁ sin θ₁ = n₂ sin θ₂ for one term. Angles between 0° and 90° (exclusive of 90°
 * as an input), indices at least 1. Returns "no-solution" when the sine would exceed 1:
 * total internal reflection, or an angle no index can produce.
 */
export function solveSnell(
  target: SnellTarget,
  known: SnellKnown
): SnellResult {
  const { n1, n2, theta1, theta2 } = known;
  if (target === "theta2") {
    return angleFrom(n1, n2, theta1);
  }
  if (target === "theta1") {
    return angleFrom(n2, n1, theta2);
  }
  if (!(validAngle(theta1) && validAngle(theta2))) {
    return { ok: false, error: "invalid-angle" };
  }
  const known1 = target === "n2" ? n1 : n2;
  if (!validIndex(known1)) {
    return { ok: false, error: "invalid-index" };
  }
  return target === "n2"
    ? solveIndex(known1 * Math.sin(theta1 * RAD), Math.sin(theta2 * RAD))
    : solveIndex(known1 * Math.sin(theta2 * RAD), Math.sin(theta1 * RAD));
}

/** The angle on the other side of the boundary, from the index and angle on this side. */
function angleFrom(
  nHere: number | undefined,
  nThere: number | undefined,
  theta: number | undefined
): SnellResult {
  if (!(validIndex(nHere) && validIndex(nThere))) {
    return { ok: false, error: "invalid-index" };
  }
  if (!validAngle(theta)) {
    return { ok: false, error: "invalid-angle" };
  }
  const sine = (nHere * Math.sin(theta * RAD)) / nThere;
  return sine > 1
    ? { ok: false, error: "no-solution" }
    : { ok: true, value: Math.asin(sine) * DEG };
}

function solveIndex(numerator: number, sine: number): SnellResult {
  if (sine === 0) {
    return { ok: false, error: "no-solution" };
  }
  const n = numerator / sine;
  return n >= 1 ? { ok: true, value: n } : { ok: false, error: "no-solution" };
}

/** The angle of incidence beyond which light going from n₁ into n₂ cannot enter (n₁ > n₂). */
export function criticalAngle(n1: number, n2: number): number | null {
  if (!(n1 > n2 && n2 > 0)) {
    return null;
  }
  return Math.asin(n2 / n1) * DEG;
}

/** The angle of incidence at which reflected light is fully polarised: arctan(n₂ ÷ n₁). */
export function brewsterAngle(n1: number, n2: number): number {
  return Math.atan(n2 / n1) * DEG;
}

export interface Reflectance {
  /** Perpendicular (s) polarisation. */
  s: number;
  /** Parallel (p) polarisation. */
  p: number;
  /** Unpolarised light: the mean of s and p. */
  unpolarised: number;
}

/** Fresnel reflectance at the interface; 1 beyond the critical angle. */
export function fresnelReflectance(
  n1: number,
  n2: number,
  theta1: number
): Reflectance {
  const sin2 = (n1 * Math.sin(theta1 * RAD)) / n2;
  if (sin2 >= 1) {
    return { s: 1, p: 1, unpolarised: 1 };
  }
  const cos1 = Math.cos(theta1 * RAD);
  const cos2 = Math.sqrt(1 - sin2 * sin2);
  const s = ((n1 * cos1 - n2 * cos2) / (n1 * cos1 + n2 * cos2)) ** 2;
  const p = ((n1 * cos2 - n2 * cos1) / (n1 * cos2 + n2 * cos1)) ** 2;
  return { s, p, unpolarised: (s + p) / 2 };
}

/** Speed of light in a medium, m/s. */
export function speedInMedium(n: number): number {
  return 299_792_458 / n;
}
